import { useRef, useLayoutEffect, useMemo } from "react";
import {
  Box3,
  DoubleSide,
  Group,
  Mesh,
  Matrix4,
  BufferGeometry,
  type MeshStandardMaterialProperties,
} from "three";
import { type Euler, type Vector3 } from "@react-three/fiber";
import type { ModelDimensions } from "../types";

interface Props {
  position: Vector3;
  rotation: Euler;
  hidden?: boolean;
  geometry: BufferGeometry;
  materialProps: Partial<MeshStandardMaterialProperties>;
  onLoaded: (dims: ModelDimensions, mesh: Mesh, group: Group) => void;
}

/**
 * Object3D
 *
 * Responsibilities:
 * 1. Center the geometry
 * 2. Compute its dimensions
 * 3. Render it with a material
 *
 * Important 3D concepts:
 * - STL files often are NOT centered at (0,0,0)
 * - Cameras work best when models are centered
 * - So we compute the bounding box and shift the geometry
 */
export function Object3D({
  hidden,
  geometry,
  materialProps: { opacity = 1, ...otherMaterialProps },
  onLoaded,
  position,
  rotation,
}: Props) {
  const meshRef = useRef<Mesh>(null);
  const groupRef = useRef<Group>(null);

  /**
   * We clone the geometry to avoid mutating the original STL.
   * Modifying shared geometry can cause unexpected bugs.
   */
  const processedGeometry = useMemo(() => {
    const geo = geometry.clone();

    /**
     * Compute spatial bounds of the model.
     * BoundingBox gives us min/max XYZ coordinates.
     */
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    geo.computeVertexNormals();

    const box = geo.boundingBox ?? new Box3();
    const { min, max } = box;

    const width = max.x - min.x;
    const length = max.y - min.y;
    const height = max.z - min.z;

    /**
     * Translate geometry so its center sits at (0,0,0).
     * This makes camera math predictable.
     */
    geo.applyMatrix4(
      new Matrix4().makeTranslation(
        -min.x - width / 2,
        -min.y - length / 2,
        -min.z - height / 2
      )
    );

    return geo;
  }, [geometry]);

  /**
   * Once geometry is processed,
   * notify parent with model dimensions.
   */
  useLayoutEffect(() => {
    if (!meshRef.current || !groupRef.current) return;

    const box = processedGeometry.boundingBox ?? new Box3();
    const { min, max } = box;

    const dims = {
      width: max.x - min.x,
      length: max.y - min.y,
      height: max.z - min.z,
      boundingRadius: processedGeometry.boundingSphere?.radius ?? 0,
    };

    onLoaded(dims, meshRef.current, groupRef.current);
  }, [processedGeometry, onLoaded]);

  return (
    <group ref={groupRef}>
      <mesh
        ref={meshRef}
        castShadow
        position={position}
        rotation={rotation}
        geometry={processedGeometry}
      >
        <meshStandardMaterial
          /**
           * DoubleSide means both sides of polygons render.
           * Useful for STL models that may not have proper normals.
           */
          side={DoubleSide}
          opacity={hidden ? 0 : opacity}
          transparent={opacity < 1 || hidden}
          {...otherMaterialProps}
        />
      </mesh>
    </group>
  );
}
